import { lazy, Suspense, useState, type KeyboardEvent } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { ApiError } from '@/lib/api'
import { statsApi, type StatsPeriod, type Dashboard } from '@/lib/statsApi'
import { moderationApi } from '@/lib/moderationApi'
import { fmtCompact, fmtHours, fmtInt, pct, shortDay } from '@/lib/statsFormat'
import { CohortTable, FunnelBars, TimeChart } from '@/components/stats/charts'
import { cx } from '@/lib/cx'
import { Card, DaysTable, GuildsTable, Overview, SourcesTable, Tile } from './sections'
import styles from './StatsPage.module.css'

/*
 * Общая статистика для /stats и настроек модерации. Период передаётся обеим вкладкам;
 * текущие показатели и последние недельные когорты подписаны отдельно.
 */
const RANGES = [
  { days: 7, label: '7 дней', prev: 'к прошлой неделе' },
  { days: 30, label: '30 дней', prev: 'к прошлым 30 дням' },
  { days: 90, label: '90 дней', prev: 'к прошлым 90 дням' },
  { days: 365, label: 'Год', prev: 'к прошлому году' },
]
const RANGE_KEY = 'klichat.stats.days'

// «Люди и серверы» — отдельный кусок сборки: раскладка графов (d3-force) нужна только там
const PeopleTab = lazy(() => import('./PeopleTab'))

type Tab = 'overview' | 'people'
const TABS: { tab: Tab; label: string }[] = [
  { tab: 'overview', label: 'Обзор' },
  { tab: 'people', label: 'Люди и серверы' },
]

function savedRange(): number {
  try {
    const v = Number(localStorage.getItem(RANGE_KEY))
    return RANGES.some((r) => r.days === v) ? v : 30
  } catch {
    return 30
  }
}

const total = (xs: number[]) => xs.reduce((a, b) => a + b, 0)

export function StatsPage({ embedded = false }: { embedded?: boolean }) {
  const [days, setDays] = useState(savedRange)
  const [custom, setCustom] = useState<{ from: string; to: string } | null>(null)
  const today = new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Moscow' }).format(new Date())
  const [from, setFrom] = useState(() => new Date(Date.parse(today) - (days - 1) * 86400000).toISOString().slice(0, 10))
  const [to, setTo] = useState(today)
  const [rangeError, setRangeError] = useState('')
  const period: StatsPeriod = custom ?? days
  const applyRange = (e: React.FormEvent) => {
    e.preventDefault()
    const length = (Date.parse(to) - Date.parse(from)) / 86400000 + 1
    if (!from || !to || !Number.isFinite(length) || length < 1 || length > 730 || to > today || from < '2015-01-01') {
      setRangeError('Выберите от 1 до 730 дней, не позже сегодняшнего дня.')
      return
    }
    setRangeError('')
    setCustom({ from, to })
  }
  // вкладка — в адресе (#people): ссылку можно отправить, обновление страницы её не сбрасывает
  const [tab, setTab] = useState<Tab>(() => (!embedded && window.location.hash === '#people' ? 'people' : 'overview'))
  const dash = useQuery({ queryKey: ['stats-dashboard', period], queryFn: () => statsApi.dashboard(period), placeholderData: keepPreviousData, refetchInterval: 5 * 60_000 })
  const live = useQuery({ queryKey: ['stats-live'], queryFn: statsApi.live, refetchInterval: 30_000, enabled: tab === 'overview' })
  const pickTab = (t: Tab) => {
    setTab(t)
    if (!embedded) window.history.replaceState(null, '', t === 'people' ? '#people' : window.location.pathname)
  }
  const onTabKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
    e.preventDefault()
    const next = TABS[(TABS.findIndex((t) => t.tab === tab) + (e.key === 'ArrowRight' ? 1 : TABS.length - 1)) % TABS.length]!.tab
    pickTab(next)
    document.getElementById(`stats-tab-${next}`)?.focus()
  }
  const range = custom ? { prev: 'к предыдущему периоду' } : RANGES.find((r) => r.days === days) ?? RANGES[1]!
  const choose = (v: number) => {
    setCustom(null)
    setRangeError('')
    setFrom(new Date(Date.parse(today) - (v - 1) * 86400000).toISOString().slice(0, 10))
    setTo(today)
    setDays(v)
    try {
      localStorage.setItem(RANGE_KEY, String(v))
    } catch {
      /* период не запомнится — не страшно */
    }
  }

  if (dash.error instanceof ApiError && (dash.error.status === 401 || dash.error.status === 403)) {
    return (
      <main className={styles.page}>
        <div className={styles.denied}>
          <h1>Статистика — только для команды Кличата</h1>
          <a className={styles.back} href="/">Вернуться в Кличат</a>
        </div>
      </main>
    )
  }

  const d = dash.data
  const s = d?.series
  return (
    <main className={cx(styles.page, embedded && styles.embedded)}>
      <div className={styles.inner}>
        <header className={styles.header}>
          <div>
            <h1 className={styles.title}>Статистика Кличата</h1>
            <p className={styles.meta}>
              {d ? `${shortDay(d.range.from)} — ${shortDay(d.range.to)}, дни по Москве · обновлено ${new Date(d.at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}` : 'Загружаем…'}
            </p>
          </div>
          <a className={styles.back} href={embedded ? '/stats' : '/'} target={embedded ? '_blank' : undefined} rel={embedded ? 'noreferrer' : undefined}>{embedded ? 'Открыть отдельно' : 'В Кличат'}</a>
        </header>

        <div className={styles.tabs} role="tablist" aria-label="Разделы статистики">
          {TABS.map((t) => (
            <button
              key={t.tab}
              id={`stats-tab-${t.tab}`}
              type="button"
              role="tab"
              aria-selected={t.tab === tab}
              aria-controls="stats-panel"
              tabIndex={t.tab === tab ? 0 : -1}
              className={cx(styles.tab, t.tab === tab && styles.tabOn)}
              onClick={() => pickTab(t.tab)}
              onKeyDown={onTabKey}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className={styles.filters} role="group" aria-label="Период">
          {RANGES.map((r) => (
            <button key={r.days} type="button" className={cx(styles.range, !custom && r.days === days && styles.rangeOn)} aria-pressed={!custom && r.days === days} onClick={() => choose(r.days)}>
              {r.label}
            </button>
          ))}
        </div>

        <form className={styles.dateRange} onSubmit={applyRange}>
          <label>С<input type="date" aria-label="Начало периода" min="2015-01-01" max={to || today} value={from} onChange={(e) => setFrom(e.target.value)} required /></label>
          <label>По<input type="date" aria-label="Конец периода" min={from || '2015-01-01'} max={today} value={to} onChange={(e) => setTo(e.target.value)} required /></label>
          <button type="submit" className={styles.range}>Применить</button>
          {custom ? <span className={styles.meta}>Сравнение с таким же периодом перед выбранным</span> : null}
          {rangeError ? <p className={styles.error} role="alert">{rangeError}</p> : null}
        </form>
        {dash.error ? <p className={styles.error} role="alert">Не удалось загрузить выбранный период.</p> : null}
        <div id="stats-panel" role="tabpanel" aria-labelledby={`stats-tab-${tab}`}>
        {tab === 'people' ? (
          <Suspense fallback={<p className={styles.meta}>Загружаем…</p>}>
            <PeopleTab period={period} />
          </Suspense>
        ) : null}

        {tab === 'overview' && dash.error && !d ? <p className={styles.error}>Статистика не загрузилась. Обновите страницу.</p> : null}

        {tab === 'overview' && d && s ? (
          <div className={cx(styles.content, dash.isPlaceholderData && styles.stale)}>
            <Overview d={d} live={live.data} />

            <section className={styles.tiles} aria-label="Главные цифры за период">
              <Tile label="Новые пользователи" pair={d.kpi.new_users} prev={range.prev} spark={s.new_users} />
              <Tile label="Активных за период" pair={d.kpi.active} prev={range.prev} />
              <Tile label="Активных в среднем за день" pair={d.kpi.dau} prev={range.prev} spark={s.active} />
              <Tile label="Сообщений" pair={d.kpi.messages} prev={range.prev} spark={s.messages_guild.map((v, i) => v + (s.messages_dm[i] ?? 0))} />
              <Tile label="Часов в голосе" pair={d.kpi.voice_minutes} prev={range.prev} spark={s.voice_minutes} format={fmtHours} />
              <Tile label="Пик онлайна" pair={d.kpi.peak_online} prev={range.prev} spark={s.peak_online} />
              <Tile label="Новых серверов" pair={d.kpi.new_guilds} prev={range.prev} spark={s.new_guilds} />
              <Tile label="Заходов на сайт" pair={d.kpi.site_visits} prev={range.prev} spark={s.site_visits} />
            </section>

            <ProfileAdoption d={d} />
            <section className={styles.charts}>
              <Card><TimeChart title="Рост количества аккаунтов" sub={`за период: ${total(s.new_users) - total(s.deleted_users) >= 0 ? '+' : ''}${fmtInt(total(s.new_users) - total(s.deleted_users))} · на конец каждого дня, без ботов`} days={s.days} series={[{ key: 'accounts', label: 'аккаунтов', values: s.accounts, color: 'var(--chart-1)' }]} /></Card>
              <Card>
                <TimeChart title="Новые пользователи" sub={`за период ${fmtInt(d.kpi.new_users.now)}`} days={s.days} series={[{ key: 'new', label: 'новых', values: s.new_users, color: 'var(--chart-1)' }]} />
              </Card>
              <Card>
                <TimeChart title="Активные по дням" sub={`в среднем ${fmtInt(d.kpi.dau.now)} в день`} days={s.days} series={[{ key: 'active', label: 'активных', values: s.active, color: 'var(--chart-1)' }]} />
              </Card>
              <Card>
                <TimeChart
                  title="Сообщения"
                  sub={`всего ${fmtInt(d.kpi.messages.now)}`}
                  days={s.days}
                  series={[
                    { key: 'guild', label: 'на серверах', values: s.messages_guild, color: 'var(--chart-1)' },
                    { key: 'dm', label: 'в личке', values: s.messages_dm, color: 'var(--chart-2)' },
                  ]}
                />
              </Card>
              <Card>
                <TimeChart
                  title="Часы в голосе"
                  sub={`всего ${fmtHours(d.kpi.voice_minutes.now)} ч · показ экрана ${fmtHours(d.kpi.stream_minutes.now)} ч`}
                  days={s.days}
                  series={[{ key: 'voice', label: 'часов', values: s.voice_minutes.map((m) => m / 60), color: 'var(--chart-1)' }]}
                  format={(h) => (h < 10 ? h.toLocaleString('ru-RU', { maximumFractionDigits: 1 }) : fmtInt(h))}
                />
              </Card>
              <Card>
                <TimeChart title="Пик онлайна" sub={`максимум ${fmtInt(d.kpi.peak_online.now)}`} days={s.days} series={[{ key: 'peak', label: 'одновременно в сети', values: s.peak_online, color: 'var(--chart-1)' }]} />
              </Card>
              <Card>
                <TimeChart
                  title="Заходы на сайт"
                  sub={`уникальных ${fmtInt(total(s.site_uniques))}`}
                  days={s.days}
                  series={[{ key: 'visits', label: 'заходов', values: s.site_visits, color: 'var(--chart-1)' }]}
                  format={fmtCompact}
                />
              </Card>
            </section>

            <section className={styles.pair}>
              <Card>
                <h3 className={styles.cardTitle}>С сайта до учётной записи</h3>
                <p className={styles.cardSub}>за период; заходы считаются с 22 сентября, а регистрации приходят ещё по приглашениям и из приложения — шаги могут не сходиться</p>
                <FunnelBars
                  steps={[
                    { label: 'Зашли на сайт', value: total(s.site_visits) },
                    { label: 'Открыли регистрацию', value: total(s.register_opens) },
                    { label: 'Запросили код', value: total(s.signup_starts) },
                    { label: 'Зарегистрировались', value: d.kpi.new_users.now },
                  ]}
                />
              </Card>
              <Card>
                <h3 className={styles.cardTitle}>Первые шаги новичков</h3>
                <p className={styles.cardSub}>кто зарегистрировался за период; вход в голос отмечается с 23 сентября</p>
                <FunnelBars
                  steps={[
                    { label: 'Зарегистрировались', value: d.funnel.registered },
                    { label: 'Вступили в сервер', value: d.funnel.joined_server },
                    { label: 'Написали сообщение', value: d.funnel.wrote },
                    { label: 'Зашли в голос', value: d.funnel.voice },
                  ]}
                />
                <p className={styles.retention}>
                  Вернулись на следующий день: <b>{fmtInt(d.funnel.d1)}</b> из {fmtInt(d.funnel.d1_eligible)}
                  {d.funnel.d1_eligible > 0 ? ` (${pct(d.funnel.d1, d.funnel.d1_eligible)}%)` : ''}
                  <br />
                  Вернулись в течение недели: <b>{fmtInt(d.funnel.w1)}</b> из {fmtInt(d.funnel.w1_eligible)}
                  {d.funnel.w1_eligible > 0 ? ` (${pct(d.funnel.w1, d.funnel.w1_eligible)}%)` : ''}
                </p>
              </Card>
            </section>

            <Card>
              <h3 className={styles.cardTitle}>Возвращаемость по неделям</h3>
              <p className={styles.cardSub}>последние 8 недель на сегодня: доля зарегистрировавшихся, кто открывал Кличат в следующие недели</p>
              <CohortTable cohorts={d.cohorts} />
            </Card>

            <section className={styles.pair}>
              <Card>
                <h3 className={styles.cardTitle}>Самые живые серверы</h3>
                <p className={styles.cardSub}>по сообщениям за период</p>
                <GuildsTable rows={d.top_guilds} />
              </Card>
              <Card>
                <h3 className={styles.cardTitle}>Регистрации по кампаниям</h3>
                <p className={styles.cardSub}>ссылки с пометкой ?c=код</p>
                <SourcesTable rows={d.sources} />
              </Card>
            </section>

            <DaysTable s={s} />
            <ServiceSummary />
          </div>
        ) : null}
        </div>
      </div>
    </main>
  )
}

function ServiceSummary() {
  const [open, setOpen] = useState(false)
  const q = useQuery({ queryKey: ['mod-stats'], queryFn: moderationApi.stats, enabled: open, refetchInterval: open ? 60_000 : false })
  const d = q.data
  return <details className={cx(styles.card, styles.details)} onToggle={(e) => setOpen(e.currentTarget.open)}>
    <summary>Сервис и платежи</summary>
    {open && q.isPending ? <p className={styles.meta}>Загружаем…</p> : null}
    {q.isError ? <p className={styles.error} role="alert">Не удалось загрузить показатели сервиса.</p> : null}
    {d ? <>
      <p className={styles.cardSub}>Текущие показатели; платежи — за последние 30 дней.</p>
      <dl className={styles.adoption}>
        <div><dt>Платежи за 30 дней</dt><dd>{fmtInt(d.money.paid_month_rub)} ₽</dd></div>
        <div><dt>Засчитанные приглашения</dt><dd>{fmtInt(d.money.referrals_qualified)}</dd></div>
        <div><dt>Подключили push-уведомления</dt><dd>{fmtInt(d.push_users)}</dd></div>
        <div><dt>Привязали почту</dt><dd>{fmtInt(d.users.with_email)}</dd></div>
        <div><dt>Удалённых аккаунтов</dt><dd>{fmtInt(d.users.deleted)}</dd></div>
        <div><dt>Серверов в каталоге</dt><dd>{fmtInt(d.guilds.listed)}</dd></div>
        {d.money.sms_balance !== null ? <div><dt>Баланс SMS Aero</dt><dd>{fmtInt(Math.round(d.money.sms_balance))} ₽</dd></div> : null}
      </dl>
    </> : null}
  </details>
}

function ProfileAdoption({ d }: { d: Dashboard }) {
  const p = d.profiles
  const a = d.achievements
  return <section className={styles.pair}>
    <Card>
      <h3 className={styles.cardTitle}>Украшения профиля · сейчас</h3>
      <p className={styles.cardSub}><b>{fmtInt(p.equipped)}</b> из {fmtInt(d.totals.users)} человек используют украшения ({pct(p.equipped, d.totals.users)}%). Истёкшие украшения Плюса не учитываются.</p>
      <dl className={styles.adoption}>
        {([['Рамки', p.frames], ['Эффекты', p.effects], ['Плашки', p.plates], ['Стиль ника', p.names], ['Цвета профиля', p.themes], ['Выбрали витрину', p.showcases], ['Тег сервера', p.tags], ['Есть полученные украшения', p.reward_owners]] as const).map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{fmtInt(value)}</dd></div>)}
      </dl>
    </Card>
    <Card>
      <h3 className={styles.cardTitle}>Достижения</h3>
      <p className={styles.cardSub}>Есть у {fmtInt(a.people)} человек. За выбранный период: {fmtInt(a.period_earned)} достижений у {fmtInt(a.period_people)} человек.</p>
      <div className={styles.tableWrap}><table className={styles.table}>
        <thead><tr><th>Достижение</th><th>Получили</th><th>За период</th><th>В процессе</th></tr></thead>
        <tbody>{a.rows.map((r) => <tr key={r.id}><td>{r.name}</td><td>{fmtInt(r.earned)}</td><td>{fmtInt(r.period)}</td><td>{fmtInt(r.progressing)}</td></tr>)}</tbody>
      </table></div>
    </Card>
  </section>
}
